import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import Header from "./components/utils/header/HeaderPage";
import SideNav from "./components/utils/SideNav";

import EmployeeHomepage from "./components/EmployeeDashboard/EmployeeHomepage";
import Login from "./components/Account/LoginPage";
import Summary from "./features/simulations/components/Summary/SummaryPage";
import Transfer from "./features/simulations/components/TransferDashboard/TransferPage";
import Production from "./features/simulations/components/ProductionDashboard/ProductionPage";
import Consumption from "./features/simulations/components/ConsumptionDashboard/ConsumptionPage";
import Storage from "./features/simulations/components/StorageDashboard/StorageChartPage";
import Verification from "./features/simulations/components/VerificationDashboard/VerificationPage";
import DemandCharge from "./features/simulations/components/DemandChargeDashboard/DemandChargePage";
import Device from "./features/devices/components/DevicesPage";
import SimulationDevices from "./features/devices/components/SimulationDevicesPage";
import Settings from "./components/Settings/SettingsPage";
import UploadFiles from "./features/simulations/components/FileUpload/UploadFiles";
import UserSettingsPage from "./components/UserSettings/UserSettingsPage";
import ProtectedRoute from "./components/utils/ProtectedRoute";
import { UserSessionProvider } from "./session/UserSessionContext";
import SessionPopup from "./components/utils/SessionPopup";
import "./App.css";
import HistoryImport from "./features/simulations/components/FileUpload/HistoryImport";
import ScrollToTop from "./ScrollToTop";

import MicrogridsPage from "./features/microgrids/components/MicrogridsPage";

import SimulationResults from "./features/simulations/components/Microgrid/SimulationResults";
import Simulations from "./features/simulations/components/Microgrid/NewUploadFiles";
import { ToastContainer } from "react-toastify";
import RegisterForm from "./components/Account/Registration";
import BranchesPage from "./features/branches/components/BranchesPage";
import Employees from "./components/Account/EmployeeListPage";
import EmployeesPage from "./features/users/components/EmployeesPage";
import CustomersPage from "./features/users/components/CustomersPage";
import "react-toastify/dist/ReactToastify.css";

import Sidenav_DataFile from "./datafiles/data_utils_sidenav.json";
import { useNavigation } from "./contexts/NavigationContext";
import { useUserSession } from "./session/useUserSession";
import { isNonProductionEnvironment } from "./utils/env";
import CopilotGate from "./features/copilot/components/CopilotGate";

// Sidenav items visibility configuration
// Tier 0 (no branch): Branch Homepage, Settings
// Tier 1 (branch selected): + Employees, Customers
// Tier 2 (customer selected): + Microgrid
// Tier 3 (microgrid selected): + Devices, Simulations, Data
const ALWAYS_VISIBLE = new Set(["Branch Homepage", "Settings"]);
const REQUIRES_BRANCH = new Set(["Employees", "Customers"]);
const REQUIRES_CUSTOMER = new Set(["Microgrid"]);
const REQUIRES_MICROGRID = new Set(["Devices", "Simulations", "Data"]);

// Role-based sidenav visibility (null = all items allowed)
const ROLE_VISIBLE_ITEMS = {
  "Customer": new Set(["Microgrid", "Simulations", "Settings"]),
  "Employee": new Set(["Employees", "Customers", "Microgrid", "Devices", "Simulations", "Settings"]),
  "Branch Admin": null,
  "Organization Admin": null,
};
const DEFAULT_VISIBLE_ITEMS = new Set(["Settings"]);

// Helper function to conditionally render Sidebar and Header
export const Layout = ({ children }) => {
  const location = useLocation();
  const { navigationTier, selectedSimulation } = useNavigation();
  const { userSession } = useUserSession();
  const jobTitle = userSession?.user?.jobTitle || sessionStorage.getItem("jobTitle");

  const [hasDemandCharge, setHasDemandCharge] = useState(
    () => sessionStorage.getItem("hasDemandCharge") === "1",
  );
  useEffect(() => {
    const check = () =>
      setHasDemandCharge(sessionStorage.getItem("hasDemandCharge") === "1");
    check();
    window.addEventListener("storage", check);
    const interval = setInterval(check, 2000);
    return () => {
      window.removeEventListener("storage", check);
      clearInterval(interval);
    };
  }, [selectedSimulation]);
  const allowedByRole = jobTitle && jobTitle in ROLE_VISIBLE_ITEMS
    ? ROLE_VISIBLE_ITEMS[jobTitle]
    : DEFAULT_VISIBLE_ITEMS;

  // Normalize path so "/" and "/#/" (and other hash router edge-cases) behave identically.
  const rawPath = location?.pathname ?? "/";
  const normalizedPath = (rawPath.replace(/\/+$/, "") || "/") || "/";

  // Detect MSAL redirect state - after OAuth, the URL contains #state=...&code=...
  // which HashRouter parses as a weird path. We should treat this as an auth/loading state.
  const isMsalRedirect = window.location.hash.includes("state=") && 
                         (window.location.hash.includes("code=") || window.location.hash.includes("error="));

  const authRoutes = new Set([
    "/login",
    "/",
    "/forgot-password",
    "/register",
    "/reset-password",
  ]);
  const isAuthRoute = authRoutes.has(normalizedPath) || isMsalRedirect;

  const showHeader = !isAuthRoute;
  const showSideNav = !isAuthRoute;

  // Page content should be auth if no header/sidenav.
  const contentClassName = isAuthRoute ? "auth-content" : "content";


  const noSidebarAndHeaderRoutes = [
    "/forgot-password",
    "/register",
    "/reset-password",
    "/",
    "/#/",
  ];
  const noSidebarRoutes = [
    // "/employeehomepage",
    // "/employee-settings",
    // "/#/employeehomepage",
    // "/#/microgrid-page-design",
    // "/microgrid-page-design",
    // "/device-config",
    "/#/device-config",
    "/#/microgrid/meter/simulations",
    "/microgrid/meter/simulations",
    "/#/generate-simulation",
    "/generate-simulation",
    "/simulation-data",
    "/#/simulation-data",
    "/#/branchhomepage",
    "/branchhomepage",
    "#/employees",
    "/employees",
    "/customers",
    "/device",
    "/#/device",
  ];


  // Keep sidenav data in the same scope where it's used to avoid no-undef issues.
  const sidenavData = Sidenav_DataFile?.sidenav_data ?? { items: [] };

  // Filter sidenav items based on role + navigation tier (progressive disclosure)
  const filteredSidenavItems = useMemo(() => {
    const baseItems = sidenavData.items.filter((item) => {
      const label = item.label;
      if (allowedByRole && !allowedByRole.has(label)) return false;
      if (ALWAYS_VISIBLE.has(label)) return true;
      if (REQUIRES_BRANCH.has(label)) return navigationTier >= 1;
      if (REQUIRES_CUSTOMER.has(label)) return navigationTier >= 2;
      if (REQUIRES_MICROGRID.has(label)) return navigationTier >= 3;
      return false;
    });

    // If a simulation is selected, add a divider and the dynamic dropdown
    if (selectedSimulation) {
      baseItems.push({ type: "divider" });
      const label = `${selectedSimulation.name} (${selectedSimulation.startDate} to ${selectedSimulation.endDate})`;
      const simulationSubitems = [
        { label: "Summary", path: "/summary", icon: "FaThLarge" },
        { label: "Devices", path: "/device", icon: "FaMobile" },
        { label: "Storage", path: "/storage", icon: "FaBatteryHalf" },
        { label: "Production", path: "/production", icon: "FaSun" },
        { label: "Consumption", path: "/consumption", icon: "FaLightbulb" },
        { label: "Transfer", path: "/transfer", icon: "FaExchangeAlt" },
      ];
      if (hasDemandCharge) {
        simulationSubitems.push({
          label: "Demand Charge",
          path: "/demand-charge",
          icon: "FaBolt",
        });
      }
      // Verification is a manual-QA aid; only expose it outside of Prod.
      if (isNonProductionEnvironment()) {
        simulationSubitems.push({
          label: "Verification",
          path: "/verification",
          icon: "FaBalanceScale",
        });
      }
      baseItems.push({
        label,
        type: "header__submenu",
        className: "simulation-dropdown",
        subitems: simulationSubitems,
      });
    }

    return baseItems;
  }, [sidenavData.items, navigationTier, selectedSimulation, allowedByRole, hasDemandCharge]);

  return (
    <>
    {showHeader && (
      <Header
        header="Dashboard"
        userProfile={{ name: "Demo Employee" }}
      />
    )}
    {showSideNav ? (
      <div className="app-body d-flex">
        <SideNav items={filteredSidenavItems} title="" />
        <div className={contentClassName}>{children}</div>
      </div>
    ) : (
      <div className={contentClassName}>{children}</div>
    )}
    {showSideNav && <CopilotGate />}
    </>
  );
};

const App = () => {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <UserSessionProvider>
              <Layout>
              <SessionPopup />
              <ScrollToTop />
              <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
              />
              <Routes>
                {/* <Route path="/login" element={<Login />} /> */}
                {/* <Route path="/forgot-password" element={<ForgotPassword />} /> */}
                {/* <Route path="/reset-password" element={<ResetPassword />} /> */}
                <Route path="/register" element={< RegisterForm />} />
            
                <Route path="/branchhomepage" element={
                  <ProtectedRoute allowedRoles={["Organization Admin", "Branch Admin"]}>
                    <BranchesPage />
                  </ProtectedRoute>
                } />
                
                <Route path="/employees" element={
                  <ProtectedRoute allowedRoles={["Organization Admin", "Branch Admin", "Employee"]}>
                    <EmployeesPage />
                  </ProtectedRoute>
                } />
                <Route path="/customer-projects" element={
                  <ProtectedRoute allowedRoles={["Organization Admin", "Branch Admin", "Employee"]}>
                    <CustomersPage />
                  </ProtectedRoute>
                } />
                <Route path="/customers" element={
                  <ProtectedRoute>
                    <EmployeeHomepage />
                  </ProtectedRoute>
                } />
                {/* Public Routes */}
                <Route path="/" element={<Login />} />
                {/* If a hash/path mismatch ever happens, show login instead of a blank layout */}
                <Route path="*" element={<Login />} />
                <Route
                  path="/employeehomepage"
                  element={
                    <ProtectedRoute>
                      <EmployeeHomepage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/microgrid-page-design"
                  element={
                    <ProtectedRoute>
                      <MicrogridsPage />
                    </ProtectedRoute>
                  }
                />

              
                <Route
                  path="/microgrid/meter/simulations"
                  element={<SimulationResults />}
                />
                <Route
                  path="/device-config"
                  element={<Device />}
                />
                <Route path="/generate-simulation" element={<Simulations />} />
                

                <Route
                  path="/summary"
                  element={
                    <ProtectedRoute>
                      <Summary />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/transfer"
                  element={
                    <ProtectedRoute>
                      <Transfer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/production"
                  element={
                    <ProtectedRoute>
                      <Production />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/consumption"
                  element={
                    <ProtectedRoute>
                      <Consumption />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/storage"
                  element={
                    <ProtectedRoute>
                      <Storage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/verification"
                  element={
                    <ProtectedRoute>
                      {isNonProductionEnvironment() ? (
                        <Verification />
                      ) : (
                        <Navigate to="/summary" replace />
                      )}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/demand-charge"
                  element={
                    <ProtectedRoute>
                      <DemandCharge />
                    </ProtectedRoute>
                  }
                />
                
              
                <Route
                  path="/device"
                  element={
                    <ProtectedRoute>
                      <SimulationDevices />
                    </ProtectedRoute>
                  }
                />


                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/simulation-data"
                  element={
                    <ProtectedRoute>
                      <UploadFiles />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/history"
                  element={
                    <ProtectedRoute>
                      <HistoryImport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/employee-settings"
                  element={
                    <ProtectedRoute>
                      <UserSettingsPage />
                    </ProtectedRoute>
                  }
                />
              </Routes>
              </Layout>
      </UserSessionProvider>
    </Router>
  );
}

export default App;
